// Comparing names.
//
// Almost everything this app does is matching a name from one system against a
// name from another: a Roll20 token against a tracker row, a D&D Beyond spell
// against the template table, a Compass creature against its statblock. So
// almost every module had grown its own `const norm =` — nine of them, in three
// different shapes, and which shape a given file used was invisible from the
// call site.
//
// That mattered once. A swallowed player was matched by one normalisation in the
// tracker and another in the swallow runtime; they agreed for every plain name,
// so the difference never showed up until it did. The three shapes are kept,
// because they ARE different jobs, but they are kept here with names that say
// which is which.
//
// Nothing else belongs in this file. It is deliberately boring.

/**
 * The default: collapse runs of whitespace, trim, lowercase.
 *
 * For comparing a name to a name. It keeps punctuation, so "Goblin (A)" and
 * "Goblin A" stay different creatures — which is what you want when the DM has
 * two goblins and told them apart on purpose.
 */
export const normName = (v: unknown) => String(v ?? "").replace(/\s+/g, " ").trim().toLowerCase();

/**
 * A spell name, with a rules-edition suffix taken off.
 *
 * D&D Beyond lists the older version of a reworked spell as "Aid (2014)" or
 * "… (legacy)". It is the same entry in the template table, so the suffix must
 * not decide a match.
 */
export const normSpellName = (v: unknown) =>
  normName(String(v ?? "").replace(/\s*\(\s*(2014|legacy)\s*\)\s*$/i, ""));

/**
 * Forgiving: punctuation becomes a space before the collapse.
 *
 * For the initiative tracker, where the same creature reaches us as "Behir #2",
 * "Behir (2)" and "Behir 2" depending on whether Roll20, the Compass or the DM's
 * own typing produced it. It is deliberately looser than normName and therefore
 * deliberately NOT the default: "Goblin (A)" and "Goblin A" are one name here,
 * which is right for finding a row and wrong for telling two tokens apart.
 */
export const normLoose = (v: unknown) =>
  String(v ?? "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();

/**
 * Clean a name that will be SENT to Roll20 for an EXACT-name lookup — a
 * `/w "name"` whisper target, or a `[label](~name|Ability)` ability reference.
 *
 * This is deliberately NOT normName. A lookup is matched against Roll20's REAL
 * name, so case and punctuation must SURVIVE: Roll20 knows "Hal Fling Rogge",
 * not "halflingrogge". The only job here is to remove characters that are
 * invisible at the table but defeat Roll20's exact match — the class that cost
 * a turn on 2026-10-11, when one PC's name carried an invisible leading
 * character and every turn announcement reddened the players' chat with
 * "Unable to find a player or character with name: …".
 *
 * `.trim()` and `\s` are not enough. Both already strip U+00A0 and even U+FEFF,
 * but the zero-width family (U+200B and friends) sits just past the ` - `
 * block and is category Cf, not Zs — so it survives to the lookup precisely
 * because nobody can see it to delete it.
 */
export const cleanRoll20Name = (v: unknown): string =>
  // A tight allowlist of KNOWN-INVISIBLE code points, not all of category Cf:
  // broadening to Cf would catch a soft hyphen or a joiner that renders in a
  // non-Latin name, which belongs to the name and must survive. These do not
  // render and only ever break an exact match. Kept in sync, byte for byte, with
  // the connector's cleanChatName/cleanChatText (connector/roll20.js) and pinned
  // by test-clean-roll20-name.mjs + the connector's test-chat-name-clean.mjs.
  //   U+200B ZWSP · U+200C ZWNJ · U+200D ZWJ · U+200E LRM · U+200F RLM
  //   U+202A–U+202E bidi embeddings/overrides · U+2060 word joiner · U+FEFF BOM
  String(v ?? "").replace(/[​-‏‪-‮⁠﻿]/g, "").replace(/\s+/g, " ").trim();
